import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";

import DataTableBase from "../../../shared/dashboards/DataTable/DataTable";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  convertToUserTimezone,
  getEffectiveAdminTimezone,
} from "../../../../utils/dateService";
import { DATE_RANGE } from "../../../../utils/Constants";
import {
  formatSecondsAsFraudDuration,
  resolveFraudRuleTimeUnit,
} from "../../../../utils/fraudTimeUnits";

import styles from "./stylesFlaggedUsersTable.module.css";
import CustomSelect from "../../../CommonComponents/CustomSelect/CustomSelect";
import CustomFormInput from "../../../CommonComponents/CustomFormInput/CustomFormInput";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";
import {
  useGetFlaggedUsers,
  type FlaggedUsersSearchBy,
} from "../../../../queries/fraud-detection/useGetFlaggedUsers";
import { useGetFraudRules } from "../../../../queries/fraud-detection/useGetFraudRules";
import { useDownloadFlaggedUsers } from "../../../../queries/fraud-detection/useDownloadFlaggedUsers";
import { triggerBrowserDownload } from "../../../../utils/triggerBrowserDownload";
import { buildUserManagerUrl } from "../../../../utils/NavigationUtils";
import { useDebouncedSearchQuery } from "../../../../hooks/useDebouncedSearchQuery";

export type FlaggedUserRow = {
  id: string;
  flagged_at: string | null;
  user_id: string;
  username: string;
  rule_name: string;
  /** Seconds from the backend; converted to the rule's period unit on render. */
  time_to_alert_seconds: number | null;
};

const PAGE_SIZE = 20;

const DataTable = DataTableBase as React.ComponentType<{
  id?: string;
  rowKey?: string | ((record: FlaggedUserRow) => string);
  columns?: ColumnsType<FlaggedUserRow>;
  dataSource?: FlaggedUserRow[];
  pagination?: boolean | object;
  scrollPreset?: string;
  loading?: boolean;
  className?: string;
}>;

export const MOCK_FLAGGED_USERS: FlaggedUserRow[] = [
  {
    id: "flag-mock-1",
    flagged_at: "2026-04-08T18:22:00.000Z",
    user_id: "000000002",
    username: "jdoe_sample",
    rule_name: "AML Detection",
    time_to_alert_seconds: 3846,
  },
  {
    id: "flag-mock-2",
    flagged_at: "2026-04-09T09:15:33.000Z",
    user_id: "000000014",
    username: "maria_silva88",
    rule_name: "AML Detection",
    time_to_alert_seconds: 2538,
  },
  {
    id: "flag-mock-3",
    flagged_at: "2026-04-09T11:03:12.000Z",
    user_id: "000000031",
    username: "alex_k_77",
    rule_name: "High velocity deposits",
    time_to_alert_seconds: 8265,
  },
  {
    id: "flag-mock-4",
    flagged_at: "2026-04-08T22:47:00.000Z",
    user_id: "000000008",
    username: "player_one",
    rule_name: "Withdrawal pattern anomaly",
    time_to_alert_seconds: 532,
  },
  {
    id: "flag-mock-5",
    flagged_at: "2026-04-07T16:30:00.000Z",
    user_id: "000000045",
    username: "sunny_bets",
    rule_name: "AML Detection",
    time_to_alert_seconds: 14101,
  },
  {
    id: "flag-mock-6",
    flagged_at: "2026-04-09T07:01:44.000Z",
    user_id: "000000019",
    username: "rico_m",
    rule_name: "Bonus abuse watchlist",
    time_to_alert_seconds: 930,
  },
  {
    id: "flag-mock-7",
    flagged_at: "2026-04-06T13:22:19.000Z",
    user_id: "000000052",
    username: "guest_2044",
    rule_name: "High velocity deposits",
    time_to_alert_seconds: 5587,
  },
  {
    id: "flag-mock-8",
    flagged_at: "2026-04-09T14:58:00.000Z",
    user_id: "000000003",
    username: "jdoe_sample",
    rule_name: "Chargeback risk",
    time_to_alert_seconds: 221,
  },
  {
    id: "flag-mock-9",
    flagged_at: "2026-04-05T20:11:55.000Z",
    user_id: "000000067",
    username: "luna_night",
    rule_name: "Withdrawal pattern anomaly",
    time_to_alert_seconds: 15120,
  },
  {
    id: "flag-mock-10",
    flagged_at: "2026-04-09T03:40:22.000Z",
    user_id: "000000021",
    username: "tom_h_wager",
    rule_name: "AML Detection",
    time_to_alert_seconds: 1634,
  },
  {
    id: "flag-mock-11",
    flagged_at: "2026-04-08T05:09:00.000Z",
    user_id: "000000099",
    username: "vip_member_01",
    rule_name: "Geo mismatch sessions",
    time_to_alert_seconds: 3408,
  },
  {
    id: "flag-mock-12",
    flagged_at: "2026-04-04T12:00:00.000Z",
    user_id: "000000073",
    username: "quick_spin",
    rule_name: "High velocity deposits",
    time_to_alert_seconds: 59,
  },
];

export type FlaggedUsersTableProps = {
  dataSource?: FlaggedUserRow[];
  loading?: boolean;
};

const SEARCH_BY_OPTIONS: { value: FlaggedUsersSearchBy; label: string }[] = [
  { value: "user_id", label: AppStrings.USER_ID },
  { value: "email", label: AppStrings.EMAIL },
  { value: "phone", label: AppStrings.PHONE_NUMBER },
];

function getDefaultFlaggedUsersDateRange(): [Dayjs, Dayjs] {
  const tz = getEffectiveAdminTimezone();
  return [
    dayjs().tz(tz).subtract(6, "day").startOf("day"),
    dayjs().tz(tz).endOf("day"),
  ];
}

const FlaggedUsersTable: React.FC<FlaggedUsersTableProps> = ({
  dataSource: dataSourceProp,
  loading: loadingProp,
}) => {
  const [page, setPage] = useState(1);
  // NOTE: date range is tracked locally for the UI; it is not yet forwarded to
  // useGetFlaggedUsers because the API has no date filter params. Thread into
  // the query as `start_date`/`end_date` once the backend exposes them.
  const [dates, setDates] = useState<[Dayjs, Dayjs]>(
    getDefaultFlaggedUsersDateRange,
  );

  const [form] = Form.useForm<{
    searchBy: FlaggedUsersSearchBy;
    searchText: string;
  }>();

  const searchBy = Form.useWatch("searchBy", form);
  const searchText = Form.useWatch("searchText", form);

  const { effectiveSearchQuery } = useDebouncedSearchQuery(searchText ?? "", {
    delayMs: 300,
  });

  useEffect(() => {
    setPage(1);
  }, [searchBy, effectiveSearchQuery]);

  const [startDateParam, endDateParam] = useMemo<
    [string | undefined, string | undefined]
  >(() => {
    const [start, end] = dates;
    return [
      start ? start.format("YYYY-MM-DD") : undefined,
      end ? end.format("YYYY-MM-DD") : undefined,
    ];
  }, [dates]);

  const { data: hookResponse, isLoading: hookLoading } = useGetFlaggedUsers({
    page,
    page_size: PAGE_SIZE,
    search_by: searchBy,
    search_value: effectiveSearchQuery.trim() || undefined,
    start_date: startDateParam,
    end_date: endDateParam,
  });

  const dataSource = dataSourceProp ?? hookResponse?.data ?? [];
  const total = hookResponse?.total ?? dataSource.length;
  const loading = loadingProp ?? hookLoading;

  // Fetched once (React Query staleTime 60s) so we can express each user's
  // time-to-trigger in the unit of the rule that actually fired. Lookup is by
  // rule name because that's the only linkage the flagged-users API exposes.
  const { data: fraudRules } = useGetFraudRules();
  const ruleWindowByName = useMemo(() => {
    const map = new Map<string, number>();
    (fraudRules ?? []).forEach((rule) => {
      if (rule.name && typeof rule.evaluation_window_seconds === "number") {
        map.set(rule.name, rule.evaluation_window_seconds);
      }
    });
    return map;
  }, [fraudRules]);

  const handleDateRangeQuery = useCallback(() => {
    setPage(1);
  }, []);

  const { mutate: downloadFlaggedUsers, isPending: isDownloading } =
    useDownloadFlaggedUsers();

  const handleDownload = useCallback(() => {
    if (!startDateParam || !endDateParam) {
      message.warning(AppStrings.NO_DATA_AVAILABLE);
      return;
    }
    downloadFlaggedUsers(
      {
        start_date: startDateParam,
        end_date: endDateParam,
      },
      {
        onSuccess: (result) => {
          if (!result?.download_url || result.row_count === 0) {
            message.warning(AppStrings.NO_DATA_AVAILABLE);
            return;
          }
          triggerBrowserDownload(result.download_url);
        },
        onError: (err) => {
          // eslint-disable-next-line no-console -- operator-visible error; user gets message.error
          console.error("[FlaggedUsersExport]", err);
          message.error(AppStrings.FLAGGED_USERS_DOWNLOAD_FAILED);
        },
      },
    );
  }, [startDateParam, endDateParam, downloadFlaggedUsers]);

  const columns: ColumnsType<FlaggedUserRow> = useMemo(
    () => [
      {
        title: AppStrings.DATE_AND_TIME,
        dataIndex: "flagged_at",
        key: "flagged_at",
        width: 200,
        render: (value: string | null) =>
          value ? convertToUserTimezone(value) : "—",
      },
      {
        title: AppStrings.USER_ID,
        dataIndex: "user_id",
        key: "user_id",
        width: 160,
        render: (value: string) => {
          const id = value?.trim();
          if (!id) return "—";
          return (
            <a
              href={buildUserManagerUrl(id)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.userManagerLink}
              onClick={(e) => e.stopPropagation()}
            >
              {value}
            </a>
          );
        },
      },
      {
        title: AppStrings.USERNAME,
        dataIndex: "username",
        key: "username",
        width: 160,
        render: (value: string, record: FlaggedUserRow) => {
          const uid = record.user_id?.trim();
          const name = value?.trim();
          if (!uid) return name || "—";
          return (
            <a
              href={buildUserManagerUrl(uid)}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.userManagerLink}
              onClick={(e) => e.stopPropagation()}
            >
              {name || "—"}
            </a>
          );
        },
      },
      {
        title: AppStrings.RULE_NAME,
        dataIndex: "rule_name",
        key: "rule_name",
        width: 200,
      },
      {
        title: AppStrings.TIME_TO_TRIGGER_FLAG,
        dataIndex: "time_to_alert_seconds",
        key: "time_to_alert_seconds",
        width: 180,
        render: (_, record: FlaggedUserRow) => {
          const ruleSeconds = ruleWindowByName.get(record.rule_name);
          const unit = resolveFraudRuleTimeUnit(ruleSeconds);
          return formatSecondsAsFraudDuration(
            record.time_to_alert_seconds,
            unit,
          );
        },
      },
    ],
    [ruleWindowByName],
  );

  return (
    <div className={styles.flaggedUsersTable}>
      <div className={styles.flaggedUsersTableHeader}>
        <h3 className={styles.flaggedUsersTableHeaderTitle}>
          {AppStrings.FLAGGED_USERS}
        </h3>
        <div className={styles.flaggedUsersTableHeaderActions}>
          <div
            className={styles.flaggedUsersTableDatePicker}
            data-testid="flagged-users-date-picker"
          >
            <CustomDatePicker
              dates={dates}
              setDates={setDates}
              getData={handleDateRangeQuery}
              showPresets
              startRange={DATE_RANGE.LAST_7_DAYS}
            />
          </div>
          <Button
            type="default"
            className={styles.flaggedUsersTableDownload}
            icon={<DownloadOutlined />}
            aria-label="Download"
            onClick={handleDownload}
            loading={isDownloading}
          />
        </div>
      </div>
      <Form
        form={form}
        initialValues={{
          searchBy: "user_id",
          searchText: "",
        }}
      >
        <div className={styles.fraudDetectionRulesFilters}>
          <div className={styles.fraudDetectionFilterField}>
            <Form.Item name="searchBy" noStyle>
              <CustomSelect<FlaggedUsersSearchBy>
                allowClear
                data-testid="flagged-users-search-by"
                placeholder={AppStrings.NOT_SELECTED}
                options={SEARCH_BY_OPTIONS}
              />
            </Form.Item>
          </div>
          <div className={styles.fraudDetectionFilterField}>
            <Form.Item name="searchText" noStyle>
              <CustomFormInput
                allowClear
                className={styles.fraudDetectionFilterInput}
                placeholder={AppStrings.SEARCH}
                prefix={<SearchOutlined />}
              />
            </Form.Item>
          </div>
        </div>
      </Form>
      <div className={styles.flaggedUsersTableDataWrapper}>
        <DataTable
          id="fraud-detection-flagged-users"
          rowKey="id"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          pagination={
            dataSourceProp
              ? false
              : {
                  current: page,
                  pageSize: PAGE_SIZE,
                  total,
                  onChange: (p: number) => setPage(p),
                }
          }
          scrollPreset="default"
        />
      </div>
    </div>
  );
};

export default FlaggedUsersTable;
